import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { parseNum } from "@/lib/format";

export const EditableCell = ({ value, onCommit, format, allowEmpty = false, emptyLabel = "–", prefix, testId, className = "" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const start = () => {
    setDraft(value == null ? "" : String(value).replace(".", ","));
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const n = parseNum(draft);
    if (n === null) {
      if (allowEmpty && value !== null) onCommit(null);
      return;
    }
    if (Number.isNaN(n) || n < 0) {
      toast.error("Skriv inn et gyldig tall");
      return;
    }
    if (n !== value) onCommit(n);
  };

  if (editing) {
    return (
      <input
        autoFocus
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { e.stopPropagation(); setEditing(false); }
        }}
        placeholder={allowEmpty ? "tom = og over" : ""}
        className={`w-full min-w-[56px] h-9 px-1.5 rounded-md border-2 border-sky-500 bg-white font-num text-sm outline-none ${className}`}
        data-testid={`${testId}-input`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      className={`editable-cell group w-full min-w-[56px] h-9 px-1.5 rounded-md text-left font-num text-sm flex items-center justify-between gap-1 ${className}`}
      data-testid={testId}
      title="Klikk for å redigere"
    >
      <span className={value == null ? "text-slate-400 italic font-sans" : ""}>
        {prefix}
        {value == null ? emptyLabel : format ? format(value) : value}
      </span>
      <Pencil className="w-3 h-3 text-sky-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
};

export const EditableText = ({ value, onCommit, testId, className = "", maxLength = 60 }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    const v = draft.trim();
    if (v && v !== value) onCommit(v);
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { e.stopPropagation(); setEditing(false); }
        }}
        className={`h-9 px-2 rounded-md border-2 border-sky-500 bg-white outline-none ${className}`}
        data-testid={`${testId}-input`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className={`editable-cell group inline-flex items-center gap-2 px-2 -mx-2 h-9 rounded-md text-left ${className}`}
      data-testid={testId}
      title="Klikk for å endre navn"
    >
      {value}
      <Pencil className="w-3.5 h-3.5 text-sky-600 opacity-40 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};
